import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  FileText,
  ExternalLink,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  RotateCcw,
  Download,
  Stamp,
  ChevronDown,
  ChevronRight,
  Users,
  Activity,
  Bell,
  Info,
  GitBranch,
} from 'lucide-react'
import { useDocumentList, useDocumentDetail, useRecallDocument, useDownloadDocument } from '../../hooks/useDocuments'
import type { DocumentFilters } from '../../hooks/useDocuments'
import { useEmailEvents } from '../../hooks/useSignatures'
import { useOfficeDocument } from '../../hooks/useOfficeDocument'
import { MAIN_APP_URL } from '../../config'
import { SignerDetail } from '../followup/SignerDetail'
import { ActivityLog } from '../followup/ActivityLog'
import { ReminderSection } from '../followup/ReminderForm'
import { FilterBar } from '../organize/FilterBar'
import { DocumentTree } from '../related/DocumentTree'
import type { Document, DocumentStatus as DocStatus } from '../../services/documentsApi'
import type { Actor } from '../../hooks/useActors'

const statusIcons: Record<DocStatus, typeof CheckCircle> = {
  draft: Clock,
  negotiating: GitBranch,
  pending: Clock,
  partially_signed: AlertCircle,
  completed: CheckCircle,
  declined: XCircle,
  recalled: XCircle,
  expired: AlertCircle,
}

/** Badge styles: bg + text color for pill badges */
const statusBadgeStyles: Record<DocStatus, string> = {
  draft: 'bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400',
  negotiating: 'bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400',
  pending: 'bg-warning-50 dark:bg-warning-900/30 text-warning-600 dark:text-warning-400',
  partially_signed: 'bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400',
  completed: 'bg-success-50 dark:bg-success-900/30 text-success-600 dark:text-success-400',
  declined: 'bg-error-50 dark:bg-error-900/30 text-error-600 dark:text-error-400',
  recalled: 'bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400',
  expired: 'bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400',
}

/** Statuses where recall is allowed */
const recallableStatuses: DocStatus[] = ['pending', 'partially_signed', 'draft']

type DetailTab = 'signers' | 'activity' | 'reminders' | 'details' | 'related'

/**
 * Recent documents with signing status.
 * Expandable rows show per-signer details, activity log, reminders, and metadata.
 */
interface DocumentStatusListProps {
  owner?: string
  actorId?: string
  actors?: Actor[]
}

export function DocumentStatusList({ owner, actorId, actors }: DocumentStatusListProps) {
  const { t } = useTranslation()
  const [filters, setFilters] = useState<DocumentFilters>({})
  const { data, isLoading } = useDocumentList({ owner, filters })
  const recallMutation = useRecallDocument()
  const downloadMutation = useDownloadDocument()
  const { insertSignedFooter } = useOfficeDocument()
  const [confirmRecallId, setConfirmRecallId] = useState<string | null>(null)
  const [footerInserted, setFooterInserted] = useState<Set<string>>(new Set())
  const [expandedDocId, setExpandedDocId] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<DetailTab>('signers')

  const activeActor = actors?.find((a) => a.koblink_id === actorId)
  const orgKoblinkId = activeActor?.actor_type === 'ORGANIZATION' ? actorId : undefined

  const handleRecall = (koblinkId: string) => {
    recallMutation.mutate({ documentId: koblinkId, actorId }, {
      onSuccess: () => setConfirmRecallId(null),
    })
  }

  const toggleExpand = (koblinkId: string) => {
    if (expandedDocId === koblinkId) {
      setExpandedDocId(null)
    } else {
      setExpandedDocId(koblinkId)
      setActiveTab('signers')
    }
  }

  if (isLoading) {
    return (
      <div className="text-[13px] text-neutral-500 text-center py-8">
        {t('common.loading')}
      </div>
    )
  }

  if (!data?.results?.length) {
    return (
      <div className="flex flex-col items-center justify-center flex-1 py-12 text-center px-6">
        <FileText size={36} className="text-neutral-300 dark:text-neutral-600 mb-3" />
        <p className="text-[15px] font-medium text-neutral-500 dark:text-neutral-400">
          {t('status.noDocuments')}
        </p>
        <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-1">
          {t('status.noDocumentsDescription')}
        </p>
      </div>
    )
  }

  return (
    <div className="flex-1 overflow-y-auto px-3 py-3">
      <FilterBar
        actorId={actorId}
        orgKoblinkId={orgKoblinkId}
        filters={filters}
        onFiltersChange={setFilters}
      />
      <div className="space-y-2">
        {data.results.map((doc) => {
          const StatusIcon = statusIcons[doc.status] || Clock
          const canRecall = recallableStatuses.includes(doc.status)
          const isConfirming = confirmRecallId === doc.koblink_id
          const isExpanded = expandedDocId === doc.koblink_id

          return (
            <div
              key={doc.id}
              className="rounded-lg border border-neutral-200 dark:border-neutral-700 card-hover"
            >
              <div className="p-3">
                <div className="flex items-start gap-2">
                  {/* Expand toggle */}
                  <button
                    onClick={() => toggleExpand(doc.koblink_id)}
                    className="p-0.5 mt-0.5 shrink-0 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 transition-colors"
                  >
                    {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  </button>

                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100 truncate">
                      {doc.title}
                    </p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium ${statusBadgeStyles[doc.status] || ''}`}>
                        <StatusIcon size={10} />
                        {t(`status.signingStatus.${doc.status}`)}
                      </span>
                      {doc.signer_count > 0 && (
                        <span className="text-xs text-neutral-400 dark:text-neutral-500">
                          {t('status.signedBy', {
                            count: doc.signed_count,
                            total: doc.signer_count,
                          })}
                        </span>
                      )}
                    </div>
                    {/* Metadata badges */}
                    <div className="flex flex-wrap items-center gap-1 mt-0.5">
                      {doc.folder_name && (
                        <span className="text-[10px] px-1 py-0.5 rounded bg-neutral-100 dark:bg-neutral-800 text-neutral-500 dark:text-neutral-400 truncate max-w-[80px]">
                          {doc.folder_name}
                        </span>
                      )}
                      {doc.document_type && (
                        <span
                          className="text-[10px] px-1 py-0.5 rounded text-white truncate max-w-[80px]"
                          style={{ backgroundColor: doc.document_type.color || '#6B7280' }}
                        >
                          {doc.document_type.name}
                        </span>
                      )}
                      {doc.tags?.map((tag) => (
                        <span
                          key={tag.id}
                          className="inline-flex items-center gap-1 text-[10px] px-1 py-0.5 rounded bg-neutral-100 dark:bg-neutral-800 text-neutral-500 dark:text-neutral-400 truncate max-w-[80px]"
                        >
                          {tag.color && (
                            <span
                              aria-hidden="true"
                              className="inline-block w-1.5 h-1.5 rounded-full shrink-0"
                              style={{ backgroundColor: tag.color }}
                            />
                          )}
                          <span className="truncate">{tag.name}</span>
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="flex items-center gap-1 shrink-0">
                    {doc.status === 'completed' && (
                      <>
                        <button
                          onClick={() => downloadMutation.mutate({ koblinkId: doc.koblink_id, title: doc.title })}
                          disabled={downloadMutation.isPending}
                          className="p-1 rounded hover:bg-neutral-100 dark:hover:bg-neutral-800 text-neutral-400 hover:text-success-500 disabled:opacity-50 transition-colors"
                          title={t('status.downloadSigned')}
                          aria-label={t('status.downloadSigned')}
                        >
                          <Download size={14} />
                        </button>
                        <button
                          onClick={async () => {
                            try {
                              await insertSignedFooter(t('status.signedFooter', { title: doc.title }))
                              setFooterInserted((prev) => new Set(prev).add(doc.koblink_id))
                            } catch {
                              // Error handled by hook
                            }
                          }}
                          disabled={footerInserted.has(doc.koblink_id)}
                          className="p-1 rounded hover:bg-neutral-100 dark:hover:bg-neutral-800 text-neutral-400 hover:text-primary-500 disabled:text-success-500 disabled:opacity-70 transition-colors"
                          title={footerInserted.has(doc.koblink_id) ? t('status.footerInserted') : t('status.insertFooter')}
                          aria-label={footerInserted.has(doc.koblink_id) ? t('status.footerInserted') : t('status.insertFooter')}
                        >
                          <Stamp size={14} />
                        </button>
                      </>
                    )}
                    {canRecall && (
                      <button
                        onClick={() => setConfirmRecallId(isConfirming ? null : doc.koblink_id)}
                        className="p-1 rounded hover:bg-neutral-100 dark:hover:bg-neutral-800 text-neutral-400 hover:text-error-500 transition-colors"
                        title={t('status.recall')}
                        aria-label={t('status.recall')}
                      >
                        <RotateCcw size={14} />
                      </button>
                    )}
                    <a
                      href={`${MAIN_APP_URL}/documents/${doc.koblink_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-1 rounded hover:bg-neutral-100 dark:hover:bg-neutral-800 text-neutral-400 hover:text-primary-500 transition-colors"
                      title={t('status.viewInKobSign')}
                      aria-label={t('status.viewInKobSign')}
                    >
                      <ExternalLink size={14} />
                    </a>
                  </div>
                </div>

                {/* Recall confirmation */}
                {isConfirming && (
                  <div className="mt-2 pt-2 border-t border-neutral-100 dark:border-neutral-700">
                    <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-2">
                      {t('status.recallConfirm')}
                    </p>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleRecall(doc.koblink_id)}
                        disabled={recallMutation.isPending}
                        className="flex-1 py-1 px-2 text-xs rounded bg-error-500 hover:bg-error-600 disabled:opacity-50 text-white transition-colors"
                      >
                        {recallMutation.isPending ? t('common.loading') : t('status.recall')}
                      </button>
                      <button
                        onClick={() => setConfirmRecallId(null)}
                        className="flex-1 py-1 px-2 text-xs rounded border border-neutral-200 dark:border-neutral-700 text-neutral-600 dark:text-neutral-400 hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors"
                      >
                        {t('common.cancel')}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Expanded detail panel */}
              {isExpanded && (
                <ExpandedDocumentDetail
                  doc={doc}
                  activeTab={activeTab}
                  onTabChange={setActiveTab}
                  actorId={actorId || ''}
                />
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

/** Expanded detail section with tabs for signers, activity, and reminders */
function ExpandedDocumentDetail({
  doc,
  activeTab,
  onTabChange,
  actorId,
}: {
  doc: Document
  activeTab: DetailTab
  onTabChange: (tab: DetailTab) => void
  actorId: string
}) {
  const { t } = useTranslation()
  const { data: detail } = useDocumentDetail(doc.koblink_id, actorId)
  const { data: emailEvents } = useEmailEvents(
    (doc.status === 'pending' || doc.status === 'partially_signed') ? doc.koblink_id : undefined,
    actorId,
  )

  const tabs: { key: DetailTab; icon: typeof Users; label: string }[] = [
    { key: 'signers', icon: Users, label: t('followup.tabSigners') },
    { key: 'activity', icon: Activity, label: t('followup.tabActivity') },
    { key: 'reminders', icon: Bell, label: t('followup.tabReminders') },
    { key: 'details', icon: Info, label: t('metadata.title') },
    { key: 'related', icon: GitBranch, label: t('related.title') },
  ]

  return (
    <div className="border-t border-neutral-200 dark:border-neutral-700">
      {/* Tab bar */}
      <div className="flex border-b border-neutral-100 dark:border-neutral-700" role="tablist">
        {tabs.map(({ key, icon: TabIcon, label }) => (
          <button
            key={key}
            onClick={() => onTabChange(key)}
            role="tab"
            aria-selected={activeTab === key}
            className={`flex-1 flex items-center justify-center gap-1 py-1.5 text-xs transition-colors ${
              activeTab === key
                ? 'text-primary-500 border-b-2 border-primary-500 -mb-px'
                : 'text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300'
            }`}
          >
            <TabIcon size={12} />
            {label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="px-3 py-2 max-h-64 overflow-y-auto">
        {activeTab === 'signers' && (
          <div>
            {detail?.signature_requests?.length ? (
              <div className="divide-y divide-neutral-100 dark:divide-neutral-700">
                {detail.signature_requests.map((signer) => {
                  const signerEmail = emailEvents?.email_statuses?.find(
                    (s) => s.signature_request_id === signer.koblink_id,
                  )
                  return (
                    <SignerDetail
                      key={signer.id}
                      signer={signer}
                      emailStatus={signerEmail}
                      actorId={actorId}
                    />
                  )
                })}
              </div>
            ) : (
              <p className="text-[11px] text-neutral-400 py-2 text-center">
                {t('common.loading')}
              </p>
            )}
          </div>
        )}

        {activeTab === 'activity' && (
          <ActivityLog koblinkId={doc.koblink_id} actorId={actorId} />
        )}

        {activeTab === 'reminders' && (
          <ReminderSection koblinkId={doc.koblink_id} actorId={actorId} />
        )}

        {activeTab === 'details' && (
          <MetadataDetails doc={doc} />
        )}

        {activeTab === 'related' && (
          <DocumentTree koblinkId={doc.koblink_id} actorId={actorId} />
        )}
      </div>
    </div>
  )
}

/** Read-only metadata display for expanded document detail */
function MetadataDetails({ doc }: { doc: Document }) {
  const { t } = useTranslation()

  const fields: { label: string; value: string | null }[] = [
    { label: t('metadata.documentType'), value: doc.document_type?.name ?? null },
    { label: t('metadata.department'), value: doc.department?.name ?? null },
    { label: t('metadata.responsiblePerson'), value: doc.responsible_person?.display_name ?? null },
    { label: t('metadata.counterparty'), value: doc.counterparties?.join(', ') ?? null },
    {
      label: t('metadata.contractValue'),
      value: doc.contract_value
        ? `${Number(doc.contract_value).toLocaleString()} ${doc.contract_currency || 'NOK'}`
        : null,
    },
    { label: t('metadata.expiryDate'), value: doc.contract_expires_at },
    { label: t('organize.folders'), value: doc.folder_name },
    { label: t('organize.tags'), value: doc.tags?.length ? doc.tags.map((tg) => tg.name).join(', ') : null },
  ]

  const filledFields = fields.filter((f) => f.value)

  if (filledFields.length === 0) {
    return (
      <p className="text-[11px] text-neutral-400 py-3 text-center">
        {t('metadata.noDetails')}
      </p>
    )
  }

  return (
    <div className="space-y-1.5 py-1">
      {filledFields.map(({ label, value }) => (
        <div key={label} className="flex items-baseline gap-2">
          <span className="text-[10px] text-neutral-400 dark:text-neutral-500 shrink-0 w-20">
            {label}
          </span>
          <span className="text-[11px] text-neutral-900 dark:text-neutral-100 truncate">
            {value}
          </span>
        </div>
      ))}
    </div>
  )
}
